import { Box, styled } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import { Retailer } from "@/redux/types";

import { RetailerConnectBenefits } from "./RetailerConnectBenefits";
import { RetailerConnectLogin } from "./RetailerConnectLogin";
import { ResponsiveDialog } from "@brandclub/common-ui";
import { create } from "zustand";
import SwitchToGuessCheckout from "./SwitchToGuessCheckout";

export enum PopupStatus {
  WELCOME = "welcome",
  CONNECT = "connect",
  SWITCH_TO_GUEST = "switchToGuest",
}

interface DialogState {
  connectReward: number;
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  currentUsername: string;
  currentPassword: string;

  retailer?: Retailer;
  defaultStatus?: PopupStatus;

  customSubtitle?: string;
  errorBorder?: boolean;
  guestCheckoutClick?: () => void;
  checkoutIntentEstimateCost?: {
    subtotal: number;
    shippingFees: number;
    giftCard?: number;
    shippingDiscount: number;
    tax: number;
    total: number;
    serviceFee?: number;
  };
  checkoutIntentGuestEstimateCost?: {
    subtotal: number;
    shippingFees: number;
    giftCard?: number;
    shippingDiscount: number;
    tax: number;
    total: number;
    serviceFee?: number;
  };
}

const defaultState: DialogState = {
  connectReward: 0,
  onSaveCredentials: async () => {},
  currentUsername: "",
  currentPassword: "",

  retailer: undefined,
  defaultStatus: PopupStatus.WELCOME,

  customSubtitle: "",
  errorBorder: false,
  guestCheckoutClick: () => {},
  checkoutIntentEstimateCost: undefined,
  checkoutIntentGuestEstimateCost: undefined,
};

const useDialogStore = create<
  DialogState & { open: boolean; close: () => void }
>((set) => ({
  ...defaultState,
  open: false,
  close: () => set({ open: false }),
}));

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",

  flexDirection: "column",

  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.vars.palette.text.primary,
  ".retailer-connect-login-motion-container": {
    padding: "34px 24px 55px 24px",
    [theme.breakpoints.down("md")]: {
      padding: "34px 20px 22px 20px",
    },
  },
}));

const EditConnectPopup = () => {
  const {
    open,
    close,
    connectReward,
    onSaveCredentials,
    currentUsername,
    currentPassword,
    retailer,

    defaultStatus = PopupStatus.WELCOME,

    errorBorder = false,
    customSubtitle,
    checkoutIntentEstimateCost,
    checkoutIntentGuestEstimateCost,
    guestCheckoutClick,
  } = useDialogStore();
  const [status, setStatus] = useState<PopupStatus>(defaultStatus);

  useEffect(() => {
    setStatus(defaultStatus);
  }, [defaultStatus]);

  const onCloseEdit = () => {
    setStatus(defaultStatus);
    close();
  };

  const getDialogBody = () => {
    switch (status) {
      case PopupStatus.WELCOME:
        return (
          <Box
            sx={(theme) => ({
              width: "100%",
              height: "100%",
            })}
          >
            <RetailerConnectBenefits
              rewardAmount={connectReward}
              retailerId={retailer?.retailerId}
              onContinue={() => {
                setStatus(PopupStatus.CONNECT);
              }}
            />
          </Box>
        );
      case PopupStatus.SWITCH_TO_GUEST:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <SwitchToGuessCheckout
              rewardAmount={connectReward}
              retailer={retailer as Retailer}
              onRetryLogin={() => {
                setStatus(PopupStatus.CONNECT);
              }}
              onPlaceGuestOrder={() => {
                guestCheckoutClick?.();
              }}
              checkoutIntentEstimateCost={checkoutIntentEstimateCost}
              checkoutIntentGuestEstimateCost={checkoutIntentGuestEstimateCost}
            />
          </Box>
        );
      case PopupStatus.CONNECT:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <RetailerConnectLogin
              errorBorder={errorBorder}
              closeEdit={onCloseEdit}
              onSaveCredentials={onSaveCredentials}
              guestCheckoutClick={guestCheckoutClick}
              customSubtitle={customSubtitle}
              currentUsername={currentUsername}
              currentPassword={currentPassword}
              retailer={retailer}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={onCloseEdit}>
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            width: 580,
          },
          overflowY: "auto",
          maxWidth: "100%",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",

              flexDirection: "column",
            }}
            key={status}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            {getDialogBody()}
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};

EditConnectPopup.show = (props: DialogState) => {
  useDialogStore.setState({
    ...props,
    open: true,
  });
};

EditConnectPopup.close = () => {
  useDialogStore.setState({
    ...defaultState,
    open: false,
  });
};

export default EditConnectPopup;
